import { useState, useEffect, useCallback } from 'react';
import type { Menu, Order, User, CartItem } from '../types';
import { api } from '../api/client';
import {
  getMonday, addDays, toISO, formatDateShort,
  isWeekUnlocked, isOrderable, formatWeekLabel, getWeekDays,
  formatDeadline, unlockAtForMonday, WEEKDAY_NAMES,
} from '../utils/dates';
import { formatAllergens } from '../utils/allergens';
import { ChooserModal } from '../components/ChooserModal';
import { CartPanel } from '../components/CartPanel';
import { AllergenModal } from '../components/AllergenModal';

interface OrderPageProps {
  currentUser: User;
  users: User[];
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function OrderPage({ currentUser, users, addToast }: OrderPageProps) {
  const [currentMonday, setCurrentMonday] = useState(() => getMonday(new Date()));
  const [menus, setMenus] = useState<Menu[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [chooserMenu, setChooserMenu] = useState<Menu | null>(null);
  const [chooserInitialDish, setChooserInitialDish] = useState<'dish1' | 'dish2' | null>(null);
  const [showAllergenModal, setShowAllergenModal] = useState(false);
  const [guestCodes, setGuestCodes] = useState<Order[]>([]);

  const weekStart = toISO(currentMonday);

  const loadData = useCallback(async () => {
    setLoadingMenus(true);
    try {
      const [menusData, ordersData] = await Promise.all([
        api.menus.getWeek(weekStart),
        api.orders.getAll(),
      ]);
      setMenus(menusData);
      setOrders(ordersData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ladefehler';
      addToast(`Fehler beim Laden: ${message}`, 'error');
    } finally {
      setLoadingMenus(false);
    }
  }, [weekStart, addToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getMenuForDate = (iso: string) => menus.find((m) => m.dateISO === iso);

  const getCartItemForDate = (iso: string): CartItem | undefined =>
    cart.find((c) => c.dateISO === iso && c.recipientType === 'self');

  const getOrderForDate = (iso: string): Order | undefined =>
    orders.find((o) => o.dateISO === iso && o.userId === currentUser.id && o.recipientType === 'self' && !o.cancelled);

  const handleAddToCart = (item: Omit<CartItem, 'tempId'>) => {
    const tempId = crypto.randomUUID();
    setCart((prev) => [...prev, { ...item, tempId }]);
    setChooserMenu(null);
    addToast('Zum Warenkorb hinzugefügt', 'success');
  };

  const handleRemoveFromCart = (tempId: string) => {
    setCart((prev) => prev.filter((c) => c.tempId !== tempId));
  };

  const handleSubmit = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    try {
      const created = await api.orders.create(cart.map(({ tempId: _tempId, ...rest }) => rest));
      setCart([]);
      setGuestCodes(created.filter((o) => o.recipientType === 'guest' && o.guestCode));
      addToast(`${cart.length > 1 ? cart.length + ' Bestellungen' : 'Bestellung'} aufgegeben!`, 'success');
      await loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler';
      addToast(`Fehler beim Bestellen: ${message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const weekDays = getWeekDays(currentMonday);
  const weekLabel = formatWeekLabel(currentMonday);
  const isUnlocked = currentUser.isAdmin || isWeekUnlocked(currentMonday);
  const unlockDate = unlockAtForMonday(currentMonday);
  // Nur eine Woche zurück navigierbar (Vorwoche), nicht weiter in die Vergangenheit
  const canGoBack = toISO(currentMonday) > toISO(addDays(getMonday(new Date()), -7));
  const today = new Date();
  const todayISO = toISO(today);

  const isPastWeek = addDays(currentMonday, 4) < today && toISO(addDays(currentMonday, 4)) < todayISO;

  return (
    <div>
      <div className="week-nav">
        <button
          className="week-nav-btn"
          onClick={() => setCurrentMonday((m) => addDays(m, -7))}
          disabled={!canGoBack}
        >
          ←
        </button>
        <div>
          <div className="week-label">{weekLabel}</div>
          {!isUnlocked && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.188rem' }}>
              Freischaltung am {unlockDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} um 12:00 Uhr
            </div>
          )}
        </div>
        <button
          className="week-nav-btn"
          onClick={() => setCurrentMonday((m) => addDays(m, 7))}
        >
          →
        </button>
      </div>

      {loadingMenus ? (
        <div className="loading-center">
          <div className="spinner" />
          <span>Speiseplan wird geladen…</span>
        </div>
      ) : !isUnlocked ? (
        <div className="loading-center" style={{ minHeight: '12.5rem' }}>
          <div style={{ fontSize: '2.25rem' }}>🔒</div>
          <div style={{ color: 'var(--text-muted)', fontWeight: 600, textAlign: 'center' }}>
            Diese Woche ist noch nicht freigeschaltet.
            <br />
            <span style={{ fontWeight: 400, fontSize: '0.813rem' }}>
              Bestellungen ab {unlockDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} um 12:00 Uhr möglich
            </span>
          </div>
        </div>
      ) : (
        <div className="days-grid">
          {weekDays.map((day, i) => {
            const iso = toISO(day);
            const menu = getMenuForDate(iso);
            const cartItem = getCartItemForDate(iso);
            const existingOrder = getOrderForDate(iso);
            const orderable = isOrderable(iso);
            const isToday = iso === todayISO;

            return (
              <div key={iso} className="day-card">
                <div className="day-card-header">
                  <div className="day-name" style={{ color: isToday ? 'var(--accent)' : undefined, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <span>{WEEKDAY_NAMES[i]}{isToday && ' · Heute'}</span>
                    {existingOrder && (
                      <span
                        title={`Von dir bestellt: ${existingOrder.dish.name}`}
                        style={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', background: 'var(--success-text)', flexShrink: 0 }}
                      />
                    )}
                  </div>
                  <span className="date-badge">{formatDateShort(day)}</span>
                </div>

                {!menu ? (
                  <div className="day-card-locked">
                    <div className="day-card-locked-icon">🍽</div>
                    <div>Kein Speiseplan für diesen Tag</div>
                  </div>
                ) : !orderable ? (
                  <div className="day-card-locked">
                    <div className="day-card-locked-icon">{isPastWeek ? '✓' : '⏰'}</div>
                    <div>
                      {isPastWeek ? 'Vergangener Tag' : 'Bestellfrist abgelaufen'}
                      <div style={{ fontSize: '0.688rem', marginTop: '0.25rem' }}>
                        Frist war: {formatDeadline(iso)}
                      </div>
                    </div>
                    {existingOrder && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.813rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                        Bestellt: {existingOrder.dish.name}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="day-card-dishes">
                    {[menu.dish1, menu.dish2].map((dish, di) => {
                      const isSelected = cartItem?.dish.name === dish.name ||
                        existingOrder?.dish.name === dish.name;

                      return (
                        <button
                          key={di}
                          className={`dish-btn ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            setChooserMenu(menu);
                            setChooserInitialDish(di === 0 ? 'dish1' : 'dish2');
                          }}
                        >
                          <div className="dish-btn-name">{dish.name}</div>
                          {dish.diet && (
                            <div className="dish-btn-diet">
                              {dish.diet === 'vegan' ? 'vegan' : 'vegetarisch'}
                            </div>
                          )}
                          {dish.allergens.length > 0 && (
                            <div className="dish-btn-allergens">
                              Allergene: {formatAllergens(dish.allergens)}
                            </div>
                          )}
                          {(cartItem?.dish.name === dish.name) && (
                            <span className="dish-selected-badge">Ihre Auswahl</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
        <button
          className="allergen-link"
          onClick={() => setShowAllergenModal(true)}
        >
          Allergene erklärt
        </button>
      </div>

      <CartPanel
        items={cart}
        users={users}
        onRemove={handleRemoveFromCart}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      {chooserMenu && (
        <ChooserModal
          menu={chooserMenu}
          currentUser={currentUser}
          users={users}
          existingOrders={orders}
          cartItems={cart}
          initialDish={chooserInitialDish}
          onConfirm={handleAddToCart}
          onClose={() => { setChooserMenu(null); setChooserInitialDish(null); }}
        />
      )}

      {guestCodes.length > 0 && (
        <div className="modal-overlay" onClick={() => setGuestCodes([])}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Abholcodes für Gäste</div>
                <div className="modal-subtitle">Bitte dem Gast mitteilen, der Code gilt nur für den Bestelltag.</div>
              </div>
              <button className="modal-close" onClick={() => setGuestCodes([])} aria-label="Schließen" />
            </div>
            <div className="modal-body">
              {guestCodes.map((o) => (
                <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{o.recipientName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDateShort(new Date(o.dateISO + 'T00:00:00'))}</div>
                  </div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 22, letterSpacing: '0.2em', color: 'var(--accent)' }}>
                    {o.guestCode}
                  </div>
                </div>
              ))}
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => setGuestCodes([])}>Verstanden</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAllergenModal && (
        <AllergenModal onClose={() => setShowAllergenModal(false)} />
      )}
    </div>
  );
}
